import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { catalogApi, type Catalog, type Category, type CategorySEO } from '../../api/catalog';

export function CategoryEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [category, setCategory] = useState<Partial<Category>>({
    name: '',
    slug: '',
    catalog_id: '',
    description: '',
    hsn_code: '',
    gst_code: '',
    seo: {} as CategorySEO,
    tags: [],
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void catalogApi.listCatalogs().then((items) => {
      setCatalogs(items);
      if (isNew && items[0]?.id) setCategory((c) => ({ ...c, catalog_id: items[0].id }));
    });
  }, [isNew]);

  useEffect(() => {
    if (!isNew && id) {
      void catalogApi.getCategory(id).then(setCategory).catch((e) => setError(String(e)));
    }
  }, [id, isNew]);

  const save = async () => {
    if (!category.name?.trim() || !category.catalog_id) {
      setError('Name and catalog are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (isNew) {
        const created = await catalogApi.createCategory({
          catalog_id: category.catalog_id,
          parent_id: category.parent_id,
          name: category.name.trim(),
          slug: category.slug,
          code: category.code,
          description: category.description,
          hsn_code: category.hsn_code,
          gst_code: category.gst_code,
          seo: category.seo,
          tags: category.tags,
        });
        navigate(`/catalog/categories/${created.id}/edit`);
      } else if (id) {
        await catalogApi.updateCategory(id, {
          name: category.name,
          slug: category.slug,
          code: category.code,
          description: category.description,
          hsn_code: category.hsn_code,
          gst_code: category.gst_code,
          seo: category.seo,
          tags: category.tags,
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const updateSeo = (field: keyof CategorySEO, value: string) => {
    setCategory((c) => ({ ...c, seo: { ...c.seo, [field]: value } }));
  };

  return (
    <div className="mx-auto max-w-3xl p-6">
      <Link to="/catalog/categories/tree" className="text-sm text-slate-600 hover:underline">← Category tree</Link>
      <h1 className="mt-4 text-2xl font-semibold">{isNew ? 'New Category' : 'Edit Category'}</h1>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <div className="mt-6 space-y-4 rounded-xl border bg-white p-6">
        <label className="block text-sm">
          Catalog
          <select
            value={category.catalog_id ?? ''}
            onChange={(e) => setCategory((c) => ({ ...c, catalog_id: e.target.value }))}
            className="mt-1 w-full rounded-lg border px-3 py-2"
            disabled={!isNew}
          >
            {catalogs.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          Name
          <input
            value={category.name ?? ''}
            onChange={(e) => setCategory((c) => ({ ...c, name: e.target.value }))}
            className="mt-1 w-full rounded-lg border px-3 py-2"
          />
        </label>
        <label className="block text-sm">
          Slug
          <input
            value={category.slug ?? ''}
            onChange={(e) => setCategory((c) => ({ ...c, slug: e.target.value }))}
            className="mt-1 w-full rounded-lg border px-3 py-2"
          />
        </label>
        <label className="block text-sm">
          Description
          <textarea
            value={category.description ?? ''}
            onChange={(e) => setCategory((c) => ({ ...c, description: e.target.value }))}
            className="mt-1 w-full rounded-lg border px-3 py-2"
            rows={3}
          />
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            HSN code
            <input
              value={category.hsn_code ?? ''}
              onChange={(e) => setCategory((c) => ({ ...c, hsn_code: e.target.value }))}
              className="mt-1 w-full rounded-lg border px-3 py-2"
            />
          </label>
          <label className="block text-sm">
            GST code
            <input
              value={category.gst_code ?? ''}
              onChange={(e) => setCategory((c) => ({ ...c, gst_code: e.target.value }))}
              className="mt-1 w-full rounded-lg border px-3 py-2"
            />
          </label>
        </div>
        <fieldset className="rounded-lg border p-4">
          <legend className="px-1 text-sm font-medium">SEO</legend>
          <div className="mt-2 space-y-3">
            <input
              placeholder="Title"
              value={category.seo?.title ?? ''}
              onChange={(e) => updateSeo('title', e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
            />
            <textarea
              placeholder="Description"
              value={category.seo?.description ?? ''}
              onChange={(e) => updateSeo('description', e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              rows={2}
            />
            <input
              placeholder="Keywords"
              value={category.seo?.keywords ?? ''}
              onChange={(e) => updateSeo('keywords', e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
            />
          </div>
        </fieldset>
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save category'}
        </button>
      </div>
    </div>
  );
}
